import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Upload } from 'lucide-react';
import {
  CalendarToolbar,
  type CalendarView,
} from './calendar/CalendarToolbar';
import { MonthView } from './calendar/MonthView';
import { WeekView } from './calendar/WeekView';
import { DayView } from './calendar/DayView';
import { RescheduleWeekButton } from './calendar/RescheduleWeekButton';
import { WorkoutsListView } from './WorkoutsListView';
import { ImportPlanModal } from './ImportPlanModal';
import {
  addDaysUTC,
  addMonthsUTC,
  formatDateLong,
  formatMonthYear,
  formatShortDate,
  startOfDayUTC,
  startOfMonthUTC,
  startOfWeekISO,
  todayUTC,
} from './calendar/dateUtils';

/**
 * Página `/entrenos` (M2.19).
 *
 * Calendario con 4 vistas (Día / Semana / Mes / Lista) + toolbar con
 * navegación de rango. Estado:
 *  - `view`: vista activa. Default: `'month'`.
 *  - `cursor`: fecha "central" UTC. Persistente entre cambios de vista —
 *    cambiar de Semana → Día mantiene la fecha; cambiar a Mes mantiene el
 *    mes que contiene la fecha.
 *
 * Decisión: NO sincronizamos a query params para evitar reflows complicados
 * cuando el usuario navega entre vistas (queda como mejora futura).
 *
 * La carga de datos para Día/Semana/Mes es responsabilidad de cada sub-vista
 * (usan `useWorkoutsInRange` con el rango propio). La vista Lista usa su
 * propio `useWorkoutsSearch` independiente.
 */
export function WorkoutsPage(): ReactElement {
  // M2.28 — Si la URL incluye `?week=YYYY-MM-DD` (ej. tras generar plan
  // con el Coach IA), abrimos directamente en vista Semana posicionada
  // en esa fecha. Útil para que el botón "Ver →" del JobsBanner lleve al
  // usuario a la semana correcta del plan recién generado, en vez de
  // mostrarle el mes actual vacío.
  const initial = useMemo(() => readInitialFromQuery(), []);
  const [view, setView] = useState<CalendarView>(initial.view);
  const [cursor, setCursor] = useState<Date>(initial.cursor);
  // M2.47 — modal de import de plan del entrenador.
  const [showImport, setShowImport] = useState<boolean>(false);

  // Limpiamos el query param tras consumirlo para que F5 vuelva al
  // comportamiento default (semana actual).
  useEffect(() => {
    if (initial.fromQuery && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('week');
      url.searchParams.delete('view');
      window.history.replaceState({}, '', url.toString());
    }
  }, [initial.fromQuery]);

  const navDisabled = view === 'list';

  const rangeLabel = useMemo(() => {
    if (view === 'list') return 'Todos los entrenos';
    if (view === 'month') return formatMonthYear(cursor);
    if (view === 'week') {
      const weekStart = startOfWeekISO(cursor);
      const weekEnd = addDaysUTC(weekStart, 6);
      return `${formatShortDate(weekStart)} – ${formatShortDate(weekEnd)}`;
    }
    return formatDateLong(cursor);
  }, [view, cursor]);

  function goPrev() {
    setCursor((d) => {
      if (view === 'month') return addMonthsUTC(d, -1);
      if (view === 'week') return addDaysUTC(d, -7);
      return addDaysUTC(d, -1);
    });
  }

  function goNext() {
    setCursor((d) => {
      if (view === 'month') return addMonthsUTC(d, 1);
      if (view === 'week') return addDaysUTC(d, 7);
      return addDaysUTC(d, 1);
    });
  }

  function goToday() {
    if (view === 'month') {
      setCursor(startOfMonthUTC(new Date()));
    } else {
      setCursor(todayUTC());
    }
  }

  function changeView(next: CalendarView) {
    // Al cambiar a Mes, alineamos cursor al inicio del mes que contiene la
    // fecha actual para que el grid se renderice estable. Para semana y día
    // mantenemos el cursor "raw" (UTC).
    if (next === 'month') {
      setCursor((d) => startOfMonthUTC(d));
    } else if (next === 'week' || next === 'day') {
      setCursor((d) => startOfDayUTC(d));
    }
    setView(next);
  }

  function selectDayFromMonth(d: Date) {
    setCursor(startOfDayUTC(d));
    setView('day');
  }

  return (
    <section
      aria-labelledby="workouts-title"
      data-testid="workouts-page"
      className="mx-auto max-w-6xl space-y-4"
    >
      <header className="space-y-1">
        <h1
          id="workouts-title"
          className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl"
        >
          Entrenos
        </h1>
        <p className="text-sm text-slate-600">
          Calendario de actividades planificadas y completadas. Cambia entre día,
          semana, mes o listado.
        </p>
      </header>

      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <CalendarToolbar
          view={view}
          onViewChange={changeView}
          rangeLabel={rangeLabel}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
          navDisabled={navDisabled}
          extraActions={
            <div className="flex flex-wrap items-center gap-1.5">
              {(view === 'week' || view === 'day') && (
                <RescheduleWeekButton cursor={cursor} />
              )}
              {/* M2.47 — Importar plan del entrenador (siempre visible). */}
              <button
                type="button"
                onClick={() => setShowImport(true)}
                data-testid="import-plan-button"
                className="inline-flex items-center gap-1.5 rounded border border-teal-600 bg-white px-2.5 py-1 text-xs font-medium text-teal-700 hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
              >
                <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                Importar plan
              </button>
            </div>
          }
        />
        {showImport && (
          <ImportPlanModal
            defaultReferenceDate={cursor}
            onClose={() => setShowImport(false)}
          />
        )}

        <div className="mt-3">
          {view === 'month' && (
            <MonthView cursor={cursor} onSelectDay={selectDayFromMonth} />
          )}
          {view === 'week' && <WeekView cursor={cursor} />}
          {view === 'day' && <DayView cursor={cursor} />}
          {view === 'list' && <WorkoutsListView />}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// M2.28 — initial state from query params
// ---------------------------------------------------------------------------

interface InitialState {
  view: CalendarView;
  cursor: Date;
  fromQuery: boolean;
}

/**
 * Lee `?week=YYYY-MM-DD&view=week|day|month|list` de la URL una sola vez al
 * montar. Usado por el JobsBanner (M2.27) para llevar al usuario directo a
 * la semana del plan recién generado.
 */
function readInitialFromQuery(): InitialState {
  const defaultState: InitialState = {
    view: 'month',
    cursor: todayUTC(),
    fromQuery: false,
  };
  if (typeof window === 'undefined') return defaultState;
  try {
    const params = new URLSearchParams(window.location.search);
    const weekParam = params.get('week');
    const viewParam = params.get('view') as CalendarView | null;
    if (!weekParam && !viewParam) return defaultState;

    const view: CalendarView =
      viewParam === 'day' ||
      viewParam === 'week' ||
      viewParam === 'month' ||
      viewParam === 'list'
        ? viewParam
        : weekParam
          ? 'week'
          : 'month';

    let cursor = defaultState.cursor;
    if (weekParam && /^\d{4}-\d{2}-\d{2}$/.test(weekParam)) {
      const ms = Date.parse(`${weekParam}T00:00:00Z`);
      if (Number.isFinite(ms)) cursor = new Date(ms);
    }
    return { view, cursor, fromQuery: true };
  } catch {
    return defaultState;
  }
}
